import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";

import { classNames } from "@swa-ui/string";

import { Transition } from "../Transition";
import styles from "./Digit.module.scss";

const ANIMATION_DURATION_IMMEDIATE = 0.0001;
const FONT_SIZES = ["large", "medium", "small", "xlarge", "xsmall"];
const LINE_HEIGHT_RATIO = 1.5;

/**
 * Digit is a helper component to display a single digit for Timer and NumberSelector.
 */

export const Digit = React.memo((props) => {
  const { className, emphasis = true, fontSize = 30, maxNumber = 9, number = 0 } = props;
  const [forceUpdate, setForceUpdate] = useState(false);
  const currentNumber = useRef(number);
  const resetInProgress = useRef(false);

  useEffect(() => {
    currentNumber.current = Math.min(number, maxNumber);
  });

  return (
    <div {...getProps()}>
      <Transition {...getTransitionProps()}>
        <ul className={styles.numberList}>
          {renderNumbers()}
          <li>0</li>
        </ul>
      </Transition>
    </div>
  );

  function renderNumbers() {
    return Array.from({ length: maxNumber + 1 }, (_, index) => <li key={index}>{index}</li>);
  }

  function getProps() {
    return {
      className: classNames(styles.container, { [styles.emphasis]: emphasis }, className),
      style: getStyle(),
    };
  }

  function getTransitionProps() {
    const lineHeight = fontSize * LINE_HEIGHT_RATIO;
    const normalizedNumber = Math.min(number, maxNumber);
    let offset = normalizedNumber * lineHeight;

    if (normalizedNumber === maxNumber && currentNumber.current === 0) {
      offset = (maxNumber + 1) * lineHeight;
      resetInProgress.current = true;
    }

    return {
      animate: { y: -offset },
      initial: { y: -offset },
      onTransformationEnd: resetInProgress.current ? handleTransformationEnd : undefined,
      transition: {
        duration: resetInProgress.current ? ANIMATION_DURATION_IMMEDIATE : undefined,
      },
    };
  }

  function handleTransformationEnd() {
    if (resetInProgress.current) {
      resetInProgress.current = false;
      setForceUpdate(!forceUpdate);
    }
  }

  function getStyle() {
    return { "--font-size": fontSize };
  }
});

Digit.displayName = "Digit";

Digit.propTypes = {
  /**
   * Additional classes for positioning the component. Given classes may only position this component for layout
   * purposes, and cannot change how the component renders in any way.
   */
  className: PropTypes.string,

  /** Defines if the number will be bold or normal weight. */
  emphasis: PropTypes.bool,

  /** Size for numbers. */
  fontSize: PropTypes.oneOfType([PropTypes.oneOf(FONT_SIZES), PropTypes.number]),

  /**
   * Last possible number that will be displayed for the digit. This is needed because some "digit columns" will show
   * numbers 0-9 while others will only display 0-5.
   */
  maxNumber: PropTypes.number,

  /** Number that should be shown. This number is used to calculate the translateY position for the "digit column". */
  number: PropTypes.number,
};
